import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useKeepRelevantParams } from "./useKeepRelevantParams";

import { isModifierKeyPressed } from "@/utils/shortcuts";
import { useUser } from "@/context/user-context";
import { REQUEST_LOG_SOURCE_PARAM } from "@/utils/requestLogUrlParams";

const REQUEST_LOG_NAVIGATION_PARAM_KEYS = [REQUEST_LOG_SOURCE_PARAM];

export const useKeyboardNavigation = ({
  inputRef,
  items,
  getNextId,
  getPreviousId,
  scrollToLogBox,
  currentId,
  currentType,
  onGroupPage = false,
  secondarySidebarOpen = true,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[];
  getNextId: (id?: string, type?: string) => { id: string; type: string } | null;
  getPreviousId: (id?: string, type?: string) => { id: string; type: string } | null;
  scrollToLogBox: (id: string) => void;
  currentId: string;
  currentType: string;
  onGroupPage?: boolean;
  secondarySidebarOpen?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userContext = useUser();
  const keepRelevantParams = useKeepRelevantParams(REQUEST_LOG_NAVIGATION_PARAM_KEYS);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (location.pathname.includes("playground")) {
        return;
      }
      if (!secondarySidebarOpen) {
        return;
      }
      const target = event.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();

      if (["input", "textarea", "select"].includes(tagName) || target.isContentEditable) {
        return;
      }

      if (
        location.pathname.includes("/request") ||
        location.pathname.includes("/home") ||
        location.pathname.includes("/group") ||
        location.pathname.includes("/traces")
      ) {
        if (isModifierKeyPressed(event) && event.key === "k") {
          inputRef.current?.focus();
        }
        if (event.key === "j" || event.key === "ArrowDown") {
          const index = getNextId(currentId, currentType);
          if (index) {
            const path = `/workspace/${userContext?.activeWorkspaceId}/${index.type}/${index.id}`;
            navigate({
              pathname: path,
              search: keepRelevantParams(),
            });
            scrollToLogBox(index.id);
          }
        }
        if (event.key === "k" || event.key === "ArrowUp") {
          const index = getPreviousId(currentId, currentType);
          if (index) {
            const path = `/workspace/${userContext?.activeWorkspaceId}/${index.type}/${index.id}`;
            navigate({
              pathname: path,
              search: keepRelevantParams(),
            });
            scrollToLogBox(index.id);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [
    inputRef,
    items,
    getNextId,
    getPreviousId,
    scrollToLogBox,
    currentId,
    currentType,
    onGroupPage,
    secondarySidebarOpen,
    navigate,
    location,
    userContext,
    keepRelevantParams,
  ]);
};
